import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  Apartment as ApartmentIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import PropertyLoansTab from "./PropertyLoansTab";
import PropertyRentalHistoryTab from "./PropertyRentalHistoryTab";
import Gate from "../../../components/common/Gate";
import { useToggle } from "../../../contexts/FeatureToggleContext";

const CARD_TOGGLE_FLAGS = {
  loans: "property.section.loans",
  rentalHistory: "property.section.rentalHistory",
};

const CARD_WRAP_SX = {
  borderRadius: 3,
  border: "1px solid rgba(0,0,0,0.10)",
  boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
  bgcolor: "background.paper",
  overflow: "hidden",
  mb: 1.5,
};

const HEADER_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: 1.5,
  py: 0.75,
  minHeight: 40,
  backgroundColor: "#E5E7EB",
  borderBottom: "1px solid #CBD5E1",
  cursor: "pointer",
};

const TITLE_SX = {
  fontWeight: 700,
  fontSize: 12,
  lineHeight: 1.1,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#334155",
};

const HANDLE_SLOT_SX = { width: 16, mr: 0.25, flexShrink: 0 };

const DragHandle = () => (
  <DragIndicatorIcon sx={{ fontSize: 16, color: "#94A3B8", cursor: "grab", mr: 0.25, flexShrink: 0 }} />
);

const getCardState = (cardKey) => {
  try {
    const stored = localStorage.getItem("property:cardState");
    if (!stored) return true;
    const parsed = JSON.parse(stored);
    return parsed[cardKey] ?? true;
  } catch {
    return true;
  }
};

const saveCardState = (cardKey, value) => {
  try {
    const stored = localStorage.getItem("property:cardState");
    const parsed = stored ? JSON.parse(stored) : {};
    parsed[cardKey] = value;
    localStorage.setItem("property:cardState", JSON.stringify(parsed));
  } catch {}
};

const PropertyLoansAndRentalCards = ({ propertyInfo, updatePropertyInfo, allCardsCollapsed, onCollapseChange, setAllSignal, requestSetAllCards }) => {
  const loansOn = useToggle(CARD_TOGGLE_FLAGS.loans);
  const rentalOn = useToggle(CARD_TOGGLE_FLAGS.rentalHistory);

  const [isLoansCollapsed, setIsLoansCollapsed] = useState(() => getCardState("loans"));
  const [isRentalCollapsed, setIsRentalCollapsed] = useState(() => getCardState("rentalHistory"));

  useEffect(() => {
    const states = {};
    if (loansOn) states.loans = isLoansCollapsed;
    if (rentalOn) states.rentalHistory = isRentalCollapsed;
    onCollapseChange?.(states);
  }, [loansOn, rentalOn, isLoansCollapsed, isRentalCollapsed, onCollapseChange]);

  useEffect(() => {
    if (!setAllSignal || setAllSignal.target == null) return;
    const t = !!setAllSignal.target;
    setIsLoansCollapsed(t); saveCardState("loans", t);
    setIsRentalCollapsed(t); saveCardState("rentalHistory", t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAllSignal?.n]);

  const [cardOrder, setCardOrder] = useState(["loans", "rentalHistory"]);
  const dragKeyRef = useRef(null);
  const dragOverKeyRef = useRef(null);
  const localAllCollapsed = isLoansCollapsed && isRentalCollapsed;
  const isDraggable = typeof allCardsCollapsed === "boolean" ? allCardsCollapsed : localAllCollapsed;

  const handleDragStart = (key) => (e) => {
    dragKeyRef.current = key;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
    e.currentTarget.style.opacity = "0.5";
  };
  const handleDragEnter = (key) => {
    if (dragKeyRef.current === key) return;
    dragOverKeyRef.current = key;
  };
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    const from = dragKeyRef.current;
    const to = dragOverKeyRef.current;
    dragKeyRef.current = null;
    dragOverKeyRef.current = null;
    if (!from || !to || from === to) return;
    setCardOrder((prev) => {
      const next = [...prev];
      const i = next.indexOf(from);
      const j = next.indexOf(to);
      if (i === -1 || j === -1) return prev;
      const [moved] = next.splice(i, 1);
      next.splice(j, 0, moved);
      return next;
    });
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const toggleLoans = () =>
    setIsLoansCollapsed((prev) => {
      const next = !prev;
      saveCardState("loans", next);
      return next;
    });

  const toggleRental = () =>
    setIsRentalCollapsed((prev) => {
      const next = !prev;
      saveCardState("rentalHistory", next);
      return next;
    });

  const renderLoansCard = () => (
    <Box sx={CARD_WRAP_SX}>
      <Box sx={HEADER_SX} onClick={toggleLoans} onDoubleClick={(e) => { e.stopPropagation(); requestSetAllCards?.(!isLoansCollapsed); }} title="Double-click to open/close all">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {isDraggable ? <DragHandle /> : <Box sx={HANDLE_SLOT_SX} />}
          <AccountBalanceIcon sx={{ fontSize: 16, color: "#6366F1" }} />
          <Typography sx={TITLE_SX}>Loans</Typography>
        </Box>
        <IconButton size="small" sx={{ color: "#64748B", p: 0.35 }}>
          {isLoansCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>
      {!isLoansCollapsed ? (
        <Box sx={{ p: 1 }}>
          <PropertyLoansTab
            propertyInfo={propertyInfo}
            updatePropertyInfo={updatePropertyInfo}
          />
        </Box>
      ) : null}
    </Box>
  );

  const renderRentalCard = () => (
    <Box sx={CARD_WRAP_SX}>
      <Box sx={HEADER_SX} onClick={toggleRental} onDoubleClick={(e) => { e.stopPropagation(); requestSetAllCards?.(!isRentalCollapsed); }} title="Double-click to open/close all">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {isDraggable ? <DragHandle /> : <Box sx={HANDLE_SLOT_SX} />}
          <ApartmentIcon sx={{ fontSize: 16, color: "#EC4899" }} />
          <Typography sx={TITLE_SX}>Rental History</Typography>
        </Box>
        <IconButton size="small" sx={{ color: "#64748B", p: 0.35 }}>
          {isRentalCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>
      {!isRentalCollapsed ? (
        <Box sx={{ p: 1 }}>
          <PropertyRentalHistoryTab propertyInfo={propertyInfo} />
        </Box>
      ) : null}
    </Box>
  );

  const renderCard = (key) => {
    if (key === "loans") return renderLoansCard();
    if (key === "rentalHistory") return renderRentalCard();
    return null;
  };

  return (
    <>
      {cardOrder.map((key) => (
        <Gate key={key} flag={CARD_TOGGLE_FLAGS[key]}>
          <Box
            draggable={isDraggable}
            onDragStart={isDraggable ? handleDragStart(key) : undefined}
            onDragEnter={isDraggable ? () => handleDragEnter(key) : undefined}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
          >
            {renderCard(key)}
          </Box>
        </Gate>
      ))}
    </>
  );
};

export default PropertyLoansAndRentalCards;
